import { Eye, EyeOff, AlertCircle, CheckCircle, Loader2 } from "lucide-react";
import React, { useState } from "react";
import {
  forgotPassword,
  confirmForgotPassword,
} from "../../services/authService";

const ForgotPassword = ({ handleClose }) => {
  const [step, setStep] = useState(1);
  const [username, setUsername] = useState("");
  const [verificationCode, setVerificationCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const validatePassword = (password) => {
    // Minimum 8 characters, at least 1 uppercase, 1 lowercase, 1 number and 1 special character
    const passwordRegex =
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;
    return passwordRegex.test(password);
  };

  const handleUsernameSubmit = async (e) => {
    e.preventDefault();
    if (!username.trim()) {
      setError("Username is required");
      return;
    }

    setIsLoading(true);
    setError("");
    setSuccess("");
    // Simulate sending verification code
    try {
      const response = await forgotPassword(username);
      setSuccess(response.message || "Verification code sent to your email");
      setStep(2);
    } catch (error) {
      setError(
        error.message || "Failed to send verification code. Please try again."
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handlePasswordReset = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    // Validate inputs
    if (!verificationCode.trim()) {
      setError("Verification code is required");
      return;
    }

    if (!validatePassword(newPassword)) {
      setError(
        "Password must be at least 8 characters with uppercase, lowercase, numbers and special characters"
      );
      return;
    }

    if (newPassword !== confirmPassword) {
      setError("Passwords don't match");
      return;
    }

    setIsLoading(true);

    try {
      const response = await confirmForgotPassword(
        username,
        verificationCode,
        newPassword
      );
      setSuccess(response.message || "Password reset successful!");
      // Close the modal after 2 seconds on success
      setTimeout(() => {
        handleClose();
      }, 2000);
    } catch (error) {
      setError(error.message || "Failed to reset password. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-25 flex justify-center items-center z-50">
      <div className="bg-white p-8 rounded-lg w-[90%] max-w-md shadow-xl">
        <div className="mb-6">
          <h2 className="text-xl font-medium text-gray-800">Forgot Password</h2>
          <p className="text-sm text-gray-500 mt-1">
            {step === 1
              ? "Enter your username to receive a verification code"
              : "Enter the verification code and set your new password"}
          </p>
        </div>

        {/* Error and Success Messages */}
        {error && (
          <div className="mb-4 p-2 bg-red-50 border border-red-200 rounded-md">
            <p className="text-primary text-sm flex items-start">
              <AlertCircle size={16} className="mr-2 flex-shrink-0 mt-0.5" />
              {error}
            </p>
          </div>
        )}

        {success && (
          <div className="mb-4 p-2 bg-green-50 border border-green-200 rounded-md">
            <p className="text-green-600 text-sm flex items-start">
              <CheckCircle size={16} className="mr-2 flex-shrink-0 mt-0.5" />
              {success}
            </p>
          </div>
        )}

        <form
          onSubmit={step === 1 ? handleUsernameSubmit : handlePasswordReset}
        >
          {/* Username */}
          <div className="mb-4">
            <label className="block text-gray-700 mb-2 sm:text-sm text-xs">
              User Name
            </label>
            <input
              type="text"
              value={username}
              onChange={(e) => {
                setUsername(e.target.value);
                setError("");
              }}
              placeholder="username"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary text-sm sm:text-base"
              required
              disabled={step === 2}
            />
          </div>

          {step === 1 && (
            <div className="flex justify-between">
              <button
                type="submit"
                className="bg-primary text-white px-6 py-2 rounded-md focus:outline-none flex items-center justify-center disabled:opacity-70 disabled:cursor-not-allowed"
                disabled={isLoading || !username.trim()}
              >
                {isLoading ? (
                  <>
                    <Loader2 size={18} className="mr-2 animate-spin" />
                    Sending...
                  </>
                ) : (
                  "Send Code"
                )}
              </button>
              <button
                type="button"
                onClick={handleClose}
                className="text-gray-700 border px-6 py-2 rounded-md focus:outline-none hover:bg-gray-50"
              >
                Close
              </button>
            </div>
          )}

          {/* Step 2: Expanded fields */}
          {step === 2 && (
            <>
              <div className="mb-4">
                <label className="block text-gray-700 mb-2 sm:text-sm text-xs">
                  Verification Code
                </label>
                <input
                  type="text"
                  value={verificationCode}
                  onChange={(e) => {
                    setVerificationCode(e.target.value);
                    setError("");
                  }}
                  placeholder="Enter verification code"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary text-sm sm:text-base"
                  required
                />
              </div>

              <div className="mb-4">
                <label className="block text-gray-700 mb-2 sm:text-sm text-xs">
                  New Password
                </label>
                <div className="relative">
                  <input
                    type={showNewPassword ? "text" : "password"}
                    value={newPassword}
                    onChange={(e) => {
                      setNewPassword(e.target.value);
                      setError("");
                    }}
                    placeholder="Enter new password"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary text-sm sm:text-base"
                    required
                  />
                  <button
                    type="button"
                    className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-500"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                  >
                    {showNewPassword ? (
                      <Eye className="h-5" />
                    ) : (
                      <EyeOff className="h-5" />
                    )}
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Password must be at least 8 characters with uppercase,
                  lowercase, numbers and special characters
                </p>
              </div>

              <div className="mb-4">
                <label className="block text-gray-700 mb-2 sm:text-sm text-xs">
                  Confirm New Password
                </label>
                <div className="relative">
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    value={confirmPassword}
                    onChange={(e) => {
                      setConfirmPassword(e.target.value);
                      setError("");
                    }}
                    placeholder="Confirm your new password"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary text-sm sm:text-base"
                    required
                  />
                  <button
                    type="button"
                    className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-500"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  >
                    {showConfirmPassword ? (
                      <Eye className="h-5" />
                    ) : (
                      <EyeOff className="h-5" />
                    )}
                  </button>
                </div>
              </div>
            </>
          )}

          {/* Buttons for Step 2 */}
          {step === 2 && (
            <div className="flex justify-between items-center mt-6">
              <button
                type="submit"
                className="bg-primary text-white px-6 py-2 rounded-md focus:outline-none flex items-center justify-center disabled:opacity-70 disabled:cursor-not-allowed"
                disabled={
                  isLoading ||
                  !verificationCode.trim() ||
                  !newPassword.trim() ||
                  !confirmPassword.trim()
                }
              >
                {isLoading ? (
                  <>
                    <Loader2 size={18} className="mr-2 animate-spin" />
                    Resetting...
                  </>
                ) : (
                  "Reset Password"
                )}
              </button>

              <button
                type="button"
                onClick={handleClose}
                className="text-gray-700 border px-6 py-2 rounded-md focus:outline-none hover:bg-gray-50"
              >
                Close
              </button>
            </div>
          )}
        </form>
      </div>
    </div>
  );
};

export default ForgotPassword;
